import { useRef } from "react";
import { Upload, X, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROJECT_TYPES, LANGUAGES } from "@/lib/constants";

const KINDS = [["logo", "Logo"], ["image", "Immagine"], ["reference", "Screenshot / riferimento"], ["document", "Documento"]];

function guessKind(f) {
  if (!f.type.startsWith("image/")) return "document";
  return /logo/i.test(f.name) ? "logo" : "image";
}

export default function StepMaterials({ form, set }) {
  const input = useRef(null);
  const add = (list) => set({ files: [...form.files, ...Array.from(list).map((file) => ({ file, kind: guessKind(file), id: crypto.randomUUID(), url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null }))] });
  const update = (id, patch) => set({ files: form.files.map((f) => (f.id === id ? { ...f, ...patch } : f)) });
  return (
    <div className="space-y-6">
      <button type="button" onClick={() => input.current?.click()} data-testid="materials-upload-btn"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-white hover:border-sky-400 transition-colors p-10 text-center">
        <Upload className="mx-auto text-slate-400" />
        <p className="mt-3 text-sm font-medium text-slate-800">Trascina qui logo, immagini, screenshot o documenti</p>
        <p className="mt-1 text-xs text-slate-500">JPG, PNG, WEBP, SVG, PDF, DOCX · max 15 MB. Gli screenshot di riferimento vengono mostrati all'AI.</p>
      </button>
      <input ref={input} type="file" multiple hidden accept="image/*,.pdf,.doc,.docx,.txt" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="materials-file-input" />
      {form.files.length > 0 && (
        <ul className="divide-y rounded-xl border bg-white" data-testid="materials-list">
          {form.files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 p-3">
              <div className="h-12 w-16 rounded bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                {f.url ? <img src={f.url} alt="" className="w-full h-full object-contain" /> : <FileText size={18} className="text-slate-400" />}
              </div>
              <p className="flex-1 min-w-0 text-sm truncate">{f.file.name}</p>
              <Select value={f.kind} onValueChange={(k) => update(f.id, { kind: k })}>
                <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => set({ files: form.files.filter((x) => x.id !== f.id) })} aria-label="Rimuovi" className="p-1.5 text-slate-400 hover:text-red-600"><X size={16} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="rounded-xl bg-navy-900 text-white p-6" data-testid="brief-summary">
        <p className="text-xs uppercase tracking-[0.18em] text-sky-400">Riepilogo</p>
        <p className="mt-2 text-lg font-semibold">{form.name || "Progetto senza nome"}</p>
        <p className="text-sm text-slate-300">{form.company_name || "—"} · <span data-testid="summary-project-types">{form.project_types.map((v) => PROJECT_TYPES.find((t) => t.value === v)?.label).join(" + ")}</span> · {LANGUAGES.find((l) => l.value === form.language)?.label}</p>
        <p className="mt-3 text-xs text-slate-400">
          {form.start_mode === "ai" ? "L'AI genererà struttura, design e pagine interattive (richiede 2-5 minuti e consuma credito della Universal Key)."
            : form.start_mode === "template" ? "Il progetto partirà dal template selezionato." : "Il progetto partirà con una homepage vuota."}
        </p>
      </div>
    </div>
  );
}
